import Fuse from "fuse.js";

import { normalizeForSearch } from "./normalize";
import type {
    FuzzyFuseOptions,
    FuzzyMatchRange,
    FuzzySearchDoc,
    FuzzySearchHit,
    FuzzySearchIndexConfig,
    FuzzySearchParams,
    IFuzzySearchIndex,
} from "./types";

/** `matches` is `any` in fuse@3's declarations; this is the shape it actually returns. */
type RawHit<TDoc> = {
    readonly item: TDoc;
    readonly score?: number;
    readonly matches?: ReadonlyArray<{ readonly key?: string; readonly indices?: readonly FuzzyMatchRange[] }>;
};

type Cache<TSource, TDoc> = {
    readonly sourceRef: TSource;
    readonly locale: string;
    readonly fuse: Fuse<TDoc, FuzzyFuseOptions<TDoc>>;
};

const toRangesByKey = <TDoc>(hit: RawHit<TDoc>): ReadonlyMap<string, readonly FuzzyMatchRange[]> => {
    const rangesByKey = new Map<string, readonly FuzzyMatchRange[]>();

    hit.matches?.forEach(({ key, indices }) => {
        if (key && indices) {
            rangesByKey.set(key, indices);
        }
    });

    return rangesByKey;
};

/**
 * A fuzzy index over a source collection, cached on `(sourceRef, locale)` identity.
 *
 * fuse@3 builds no index at construction — the Bitap scan runs inside `search()` on every call — so
 * what the cache actually buys is `buildDocs`, which normalises every document. Rebuilding that per
 * keystroke is the cost worth avoiding; retaining the Fuse instance is incidental.
 */
export const createFuzzySearchIndex = <TSource, TId, TDoc extends FuzzySearchDoc<TId>>({
    buildDocs,
    fuseOptions,
    scoreThresholdFor,
    acceptHit,
    limit,
}: FuzzySearchIndexConfig<TSource, TId, TDoc>): IFuzzySearchIndex<TSource, TId> => {
    let cache: Cache<TSource, TDoc> | null = null;

    const ensureFuse = (source: TSource, locale: string): Fuse<TDoc, FuzzyFuseOptions<TDoc>> => {
        if (cache && cache.sourceRef === source && cache.locale === locale) {
            return cache.fuse;
        }

        const fuse = new Fuse(buildDocs(source, locale), fuseOptions);
        cache = { sourceRef: source, locale, fuse };

        return fuse;
    };

    const search = ({ source, query, locale }: FuzzySearchParams<TSource>): readonly FuzzySearchHit<TId>[] => {
        if (!query.trim()) {
            return [];
        }

        const fuse = ensureFuse(source, locale);
        const normalizedQuery = normalizeForSearch(query, locale);
        const scoreThreshold = scoreThresholdFor(normalizedQuery.length);

        const rawHits = fuse.search(normalizedQuery, { limit }) as unknown as readonly RawHit<TDoc>[];

        return rawHits
            .filter((hit) => (hit.score ?? 0) <= scoreThreshold && (acceptHit?.(hit.item, normalizedQuery) ?? true))
            .map((hit) => ({
                id: hit.item.id,
                score: hit.score ?? 0,
                rangesByKey: toRangesByKey(hit),
            }));
    };

    const invalidate = (): void => {
        cache = null;
    };

    return { search, invalidate };
};
